function setDisclosureState(
  trigger: HTMLButtonElement,
  panel: HTMLElement,
  expanded: boolean
): void {
  trigger.setAttribute('aria-expanded', String(expanded));
  panel.hidden = !expanded;
  panel.setAttribute('aria-hidden', String(!expanded));
}

function getControlledPanel(root: HTMLElement, panelId: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`#${CSS.escape(panelId)}`);
}

function initDisclosureGroup(root: HTMLElement, triggerSelector: string): void {
  const triggers = Array.from(root.querySelectorAll<HTMLButtonElement>(triggerSelector));
  const disclosures: Array<{ trigger: HTMLButtonElement; panel: HTMLElement }> = [];

  triggers.forEach(trigger => {
    const panelId = trigger.getAttribute('aria-controls');
    if (!panelId) return;

    const panel = getControlledPanel(root, panelId);
    if (!(panel instanceof HTMLElement)) return;

    disclosures.push({ trigger, panel });
  });

  let hasOpenCategory = false;

  disclosures.forEach(({ trigger, panel }) => {
    const shouldStartExpanded =
      trigger.getAttribute('aria-expanded') === 'true' && !hasOpenCategory;

    if (shouldStartExpanded) {
      hasOpenCategory = true;
    }

    setDisclosureState(trigger, panel, shouldStartExpanded);

    trigger.addEventListener('click', () => {
      const nextExpanded = trigger.getAttribute('aria-expanded') !== 'true';

      if (nextExpanded) {
        disclosures.forEach(disclosure => {
          if (disclosure.trigger === trigger) return;

          setDisclosureState(disclosure.trigger, disclosure.panel, false);
        });
      }

      setDisclosureState(trigger, panel, nextExpanded);
    });
  });
}

function updateMobileComparisonSlot(
  root: HTMLElement,
  slot: string,
  versionId: string,
  versionLabel: string
): void {
  const heading = root.querySelector<HTMLElement>(
    `[data-features-comparison-mobile-heading-slot="${slot}"]`
  );

  if (heading) {
    heading.textContent = versionLabel;
  }

  const states = Array.from(
    root.querySelectorAll<HTMLElement>(
      `[data-features-comparison-mobile-state][data-mobile-slot="${slot}"]`
    )
  );

  states.forEach(state => {
    const isActive = state.dataset.versionId === versionId;
    state.classList.toggle('is-active', isActive);
    state.setAttribute('aria-hidden', String(!isActive));
  });
}

function closeDropdown(dropdown: HTMLElement): void {
  const trigger = dropdown.querySelector<HTMLButtonElement>(
    '[data-features-comparison-select-trigger]'
  );
  const panel = dropdown.querySelector<HTMLElement>('[data-features-comparison-select-panel]');

  if (!trigger || !panel) return;

  trigger.setAttribute('aria-expanded', 'false');
  panel.hidden = true;
}

function closeOtherDropdowns(root: HTMLElement, currentDropdown: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-features-comparison-dropdown]').forEach(dropdown => {
    if (dropdown !== currentDropdown) {
      closeDropdown(dropdown);
    }
  });
}

function openDropdown(root: HTMLElement, dropdown: HTMLElement): void {
  const trigger = dropdown.querySelector<HTMLButtonElement>(
    '[data-features-comparison-select-trigger]'
  );
  const panel = dropdown.querySelector<HTMLElement>('[data-features-comparison-select-panel]');
  const selectedOption = dropdown.querySelector<HTMLButtonElement>(
    '[data-features-comparison-select-option][aria-selected="true"]'
  );

  if (!trigger || !panel) return;

  closeOtherDropdowns(root, dropdown);
  trigger.setAttribute('aria-expanded', 'true');
  panel.hidden = false;
  window.requestAnimationFrame(() => selectedOption?.focus());
}

function toggleDropdown(root: HTMLElement, dropdown: HTMLElement): void {
  const trigger = dropdown.querySelector<HTMLButtonElement>(
    '[data-features-comparison-select-trigger]'
  );

  if (trigger?.getAttribute('aria-expanded') === 'true') {
    closeDropdown(dropdown);
    trigger.focus();
    return;
  }

  openDropdown(root, dropdown);
}

function syncDropdownOptions(dropdown: HTMLElement, selectedValue: string): void {
  dropdown
    .querySelectorAll<HTMLButtonElement>('[data-features-comparison-select-option]')
    .forEach(option => {
      option.setAttribute('aria-selected', String(option.dataset.optionValue === selectedValue));
    });
}

function getSelectedLabel(select: HTMLSelectElement): string {
  return select.selectedOptions[0]?.textContent?.trim() ?? '';
}

function getSelects(root: HTMLElement): HTMLSelectElement[] {
  return Array.from(root.querySelectorAll<HTMLSelectElement>('[data-features-comparison-select]'));
}

function wouldDuplicateSelection(root: HTMLElement): boolean {
  const values = getSelects(root).map(select => select.value);

  return new Set(values).size !== values.length;
}

function initMobileSelectors(root: HTMLElement): void {
  const selects = getSelects(root);

  selects.forEach(select => {
    const slot = select.dataset.mobileSlot;
    if (!slot) return;

    let previousValue = select.value;

    const update = (): void => {
      const label = getSelectedLabel(select);

      if (wouldDuplicateSelection(root)) {
        select.value = previousValue;
        return;
      }

      previousValue = select.value;

      const dropdown = select.closest<HTMLElement>('[data-features-comparison-dropdown]');
      const value = dropdown?.querySelector<HTMLElement>('[data-features-comparison-select-value]');

      if (value) {
        value.textContent = label;
      }

      if (dropdown) {
        syncDropdownOptions(dropdown, select.value);
      }

      updateMobileComparisonSlot(root, slot, select.value, label);
    };

    select.addEventListener('change', update);
    update();
  });

  root.querySelectorAll<HTMLElement>('[data-features-comparison-dropdown]').forEach(dropdown => {
    const trigger = dropdown.querySelector<HTMLButtonElement>(
      '[data-features-comparison-select-trigger]'
    );
    const select = dropdown.querySelector<HTMLSelectElement>('[data-features-comparison-select]');
    const options = Array.from(
      dropdown.querySelectorAll<HTMLButtonElement>('[data-features-comparison-select-option]')
    );

    if (!trigger || !select) return;

    trigger.addEventListener('click', () => toggleDropdown(root, dropdown));

    trigger.addEventListener('keydown', event => {
      if (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      openDropdown(root, dropdown);
    });

    options.forEach((option, optionIndex) => {
      option.addEventListener('click', () => {
        const nextValue = option.dataset.optionValue;
        if (!nextValue) return;

        const oldValue = select.value;
        select.value = nextValue;

        if (wouldDuplicateSelection(root)) {
          select.value = oldValue;
          return;
        }

        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeDropdown(dropdown);
        trigger.focus();
      });

      option.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          closeDropdown(dropdown);
          trigger.focus();
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        event.preventDefault();

        const visibleOptions = options.filter(currentOption => !currentOption.hidden);
        const visibleIndex = visibleOptions.indexOf(option);
        const nextVisibleIndex =
          event.key === 'ArrowDown'
            ? Math.min(visibleIndex + 1, visibleOptions.length - 1)
            : Math.max(visibleIndex - 1, 0);

        visibleOptions[nextVisibleIndex]?.focus();
      });

      if (optionIndex === 0) {
        option.setAttribute('tabindex', '0');
      }
    });
  });

  document.addEventListener('click', event => {
    if (root.contains(event.target as Node)) {
      root
        .querySelectorAll<HTMLElement>('[data-features-comparison-dropdown]')
        .forEach(dropdown => {
          if (!dropdown.contains(event.target as Node)) {
            closeDropdown(dropdown);
          }
        });
      return;
    }

    root
      .querySelectorAll<HTMLElement>('[data-features-comparison-dropdown]')
      .forEach(closeDropdown);
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;

    root
      .querySelectorAll<HTMLElement>('[data-features-comparison-dropdown]')
      .forEach(closeDropdown);
  });
}

export default function featuresComparisonTable(root: HTMLElement): void {
  if (root.dataset.featuresComparisonTableInitialized === 'true') return;

  root.dataset.featuresComparisonTableInitialized = 'true';

  initDisclosureGroup(root, '[data-features-comparison-trigger]');
  initMobileSelectors(root);
}
